import { getGoogleAnalyticsClientId } from "@core/services/google-analytics";
import Button from "@core/ui/Button";
import TextField from "@core/ui/TextField";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { trim } from "lodash/fp";
import { signIn } from "next-auth/react";
import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

interface LoginFormProps {
  onClickForgotPassword: () => void;
  onSuccess: () => unknown | Promise<unknown>;
  className?: string;
  email: string;
}

const schema = yup.object({
  email: yup
    .string()
    .trim()
    .email("Enter a valid email address")
    .required("Enter your email address"),
  password: yup.string().required("Enter your password"),
});
type FormValues = yup.TypeOf<typeof schema>;

export default function LoginForm({
  onClickForgotPassword,
  onSuccess,
  className,
  email,
}: LoginFormProps): JSX.Element {
  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      email,
    },
    mode: "all",
  });

  const { register, formState } = form;
  const { errors, isSubmitting } = formState;

  const onSubmit = form.handleSubmit(
    async ({ email: formEmail, password }: FormValues) => {
      const sanitizedEmail = trim(formEmail);

      const browserData: Record<string, string> = {};

      // Try to get the user's locale, so we can track it in `nocd_v2.app_opens`.
      try {
        browserData.locale = navigator.language;
      } catch {
        /* ignore */
      }

      // Try to get the user's time zone, so we can track it in
      // `nocd_v2.app_opens`.
      try {
        browserData.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        /* ignore */
      }

      const result = await signIn("credentials", {
        redirect: false,
        email: sanitizedEmail,
        password,
        gaClientId: getGoogleAnalyticsClientId(),
        ...browserData,
      });

      if (result.ok) {
        return onSuccess();
      }

      return form.setError("email", {
        message: "Sign in failed. Check your email and password.",
      });
    }
  );

  return (
    <form
      onSubmit={onSubmit}
      className={cn("mx-auto w-full font-manrope", className)}
    >
      <h2 className="font-poppins text-center text-22px font-bold tablet:text-24px">
        Welcome back
      </h2>

      <p className="mt-2 mb-4 text-center text-16px text-gray-600">
        Please enter your password.
      </p>

      <div className="space-y-4">
        <TextField
          hideLabel
          classes={{
            input: "!bg-white !border-2 !border-gray-100",
          }}
          disabled
          id="email"
          name="email"
          type="email"
          label="Email"
          autoCorrect="off"
          autoComplete="email"
          autoCapitalize="off"
          inputMode="email"
          errorMessage={errors.email?.message}
          {...register("email")}
        />

        <TextField
          hideLabel
          classes={{
            input: "!bg-white !border-2 !border-gray-100",
          }}
          id="password"
          name="password"
          type="password"
          label="Password"
          autoComplete="current-password"
          errorMessage={errors.password?.message}
          helperText={
            <Button
              variant="text"
              onClick={onClickForgotPassword}
              color="indigo"
            >
              Forgot password?
            </Button>
          }
          {...register("password")}
        />
      </div>

      <div className="grid gap-4 mt-10">
        <Button
          type="submit"
          disabled={isSubmitting || !formState?.isValid}
          loading={isSubmitting}
          className="font-semibold h-[52px] w-full disabled:opacity-60 text-18px font-poppins"
          color="indigo"
          rounded="lg"
        >
          Log in
        </Button>
      </div>

      <div className="px-6 text-14px text-gray-800 text-opacity-60 text-center mt-4">
        By logging in, you agree to NOCD&apos;s{" "}
        <a
          className="text-blue-300"
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/terms`}
        >
          Terms &amp; Conditions
        </a>{" "}
        and{" "}
        <a
          className="text-blue-300"
          href={`${process.env.NEXT_PUBLIC_WEBSITE_BASE_URL}/privacy-policy`}
        >
          Privacy Policy
        </a>
        .
      </div>
    </form>
  );
}
